import Store from 'repatch';
import State from '../core/State';
import Work from '../entity/Work';
import User from '../entity/User';
import { UserId } from '@kaizenplatform/kaizen-id-entity';
import ErrorPresenter from '../service/ErrorPresenter';
import WorkWebApi from '../service/persistent/WorkWebApi';
import UserWebApi from '../service/persistent/UserWebApi';
import { startFetchingWorks, finishFetchingWorks, disposeWorks, setWorks, appendWorks } from '../reducer/works';
import { startFetchingUser, finishFetchingUser, disposeUser, setUser, setPublic } from '../reducer/user';

class ListWorkUsecase {
  private readonly store: Store<State>;
  private readonly errorPresenter: ErrorPresenter;
  private readonly workWebApi: WorkWebApi;
  private readonly userWebApi: UserWebApi;

  constructor({
    store,
    errorPresenter,
    workWebApi,
    userWebApi,
  }: {
    store: Store<State>;
    errorPresenter: ErrorPresenter;
    workWebApi: WorkWebApi;
    userWebApi: UserWebApi;
  }) {
    this.store = store;
    this.errorPresenter = errorPresenter;
    this.workWebApi = workWebApi;
    this.userWebApi = userWebApi;
  }

  public prepareWorks = async (userId: UserId): Promise<void> => {
    this.store.dispatch(startFetchingUser());
    this.store.dispatch(startFetchingWorks());

    let user: User;
    let works: {
      items: Work[];
      nextCursor: string | null;
    };

    try {
      user = await this.userWebApi.getUserById(userId);
    } catch (err) {
      this.store.dispatch(finishFetchingUser());
      this.store.dispatch(finishFetchingWorks());
      this.store.dispatch(disposeWorks());
      this.store.dispatch(disposeUser());
      this.errorPresenter.showAsFatal(err);
      return;
    }
    this.store.dispatch(finishFetchingUser());
    this.store.dispatch(setUser(user));

    try {
      works = await this.fetchWorks({ userId, startCursor: null });
    } catch (err) {
      this.store.dispatch(finishFetchingWorks());
      this.store.dispatch(disposeWorks());
      this.store.dispatch(setPublic(false));
      if (err.name === 'UnauthorizedError') {
        // 仕事が非公開なだけなのでエラー画面は表示しない
        return;
      }
      // 仕事取得に失敗したのでエラー画面を表示
      this.errorPresenter.showAsFatal(err);
      return;
    }
    this.store.dispatch(setPublic(true));
    this.store.dispatch(setWorks(works.items, works.nextCursor));
    this.store.dispatch(finishFetchingWorks());
  };

  public loadmore = async (): Promise<void> => {
    const user = this.store.getState().user.data;
    const startCursor = this.store.getState().works.nextCursor;
    if (!user || !startCursor) {
      return;
    }

    this.store.dispatch(startFetchingWorks());

    try {
      const { items, nextCursor } = await this.fetchWorks({ userId: user.id, startCursor });

      this.store.dispatch(appendWorks(items, nextCursor));
      this.store.dispatch(finishFetchingWorks());
    } catch (err) {
      this.errorPresenter.showAsFatal(new Error('message.fetch_data_has_failed'));

      this.store.dispatch(finishFetchingWorks());
      return;
    }
  };

  private fetchWorks = async ({
    userId,
    startCursor,
    itemCount = 12,
  }: {
    userId: string;
    startCursor: string | null;
    itemCount?: number;
  }) => {
    const { items, endCursor: nextCursor } = await this.workWebApi.getWorksById({
      itemCount,
      startCursor,
      userId,
    });

    return {
      items,
      nextCursor,
    };
  };

  public disposeWorks = (): void => {
    this.store.dispatch(disposeUser());
    this.store.dispatch(disposeWorks());
  };
}

export default ListWorkUsecase;
